/**
 * SnippetExpander
 *
 * Monitors text for snippet triggers (text starting with '/') and shows
 * a popup to expand the matching snippet. Used inside the compose editor.
 */

import { useState, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';
import type { EmailSnippet } from '@/lib/api';

interface SnippetExpanderProps {
  /** Current word being typed (extracted by parent from cursor position) */
  currentWord: string;
  /** Available snippets from useSnippets hook */
  snippets: EmailSnippet[];
  /** Called when user selects a snippet to expand */
  onExpand: (snippet: EmailSnippet) => void;
  /** Called when popup should close */
  onDismiss: () => void;
  /** Position for the popup */
  position?: { top: number; left: number };
}

export function SnippetExpander({
  currentWord,
  snippets,
  onExpand,
  onDismiss,
  position,
}: SnippetExpanderProps) {
  const [highlightIndex, setHighlightIndex] = useState(0);

  const matches = currentWord.startsWith('/')
    ? snippets.filter((s) => s.trigger.toLowerCase().startsWith(currentWord.toLowerCase()))
    : [];

  // Reset highlight when matches change
  useEffect(() => {
    setHighlightIndex(0);
  }, [currentWord]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (matches.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightIndex((prev) => Math.min(prev + 1, matches.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        onExpand(matches[highlightIndex]);
      } else if (e.key === 'Escape') {
        onDismiss();
      }
    },
    [matches, highlightIndex, onExpand, onDismiss],
  );

  useEffect(() => {
    if (matches.length > 0) {
      window.addEventListener('keydown', handleKeyDown, true);
      return () => window.removeEventListener('keydown', handleKeyDown, true);
    }
  }, [matches.length, handleKeyDown]);

  if (matches.length === 0 || !currentWord.startsWith('/')) {
    return null;
  }

  return (
    <div
      className="fixed z-50 bg-popover border border-border rounded-md shadow-lg max-h-48 overflow-y-auto min-w-[200px]"
      style={position ? { top: position.top, left: position.left } : undefined}
    >
      <div className="p-1">
        <div className="text-xs text-muted-foreground px-2 py-1">Snippets</div>
        {matches.map((snippet, idx) => (
          <button
            key={snippet.id}
            className={cn(
              'w-full text-left px-2 py-1.5 rounded text-sm hover:bg-muted transition-colors',
              idx === highlightIndex && 'bg-muted',
            )}
            onMouseDown={(e) => {
              e.preventDefault();
              onExpand(snippet);
            }}
          >
            <div className="flex items-center gap-2">
              <code className="text-xs bg-muted px-1 rounded">{snippet.trigger}</code>
              <span className="truncate text-muted-foreground text-xs">
                {snippet.body.substring(0, 50)}
                {snippet.body.length > 50 ? '...' : ''}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
